import { useQuery } from "@tanstack/react-query"

import { useAuth } from "src/context/AuthContext"

/**
 * Fetches the subscription-benefits spoken message for a specific content module.
 * Endpoint: GET /cm_subscription_benefits/<channelId>/<taskId>
 *
 * Returns the same IntroMessageResponse shape (audio_url, speech_marks, avatar_id, psk_name).
 * staleTime is set to Infinity — the message for a given task does not change frequently,
 * and playback is gated separately via MMKV so it only plays once per task.
 */
export function useUnauthCmSubscriptionBenefits(channelId: string | null, taskId: string | null) {
  const { authApi } = useAuth()

  return useQuery({
    queryKey: ["unauth", "cm_subscription_benefits", channelId, taskId],
    queryFn: async () => {
      const response = await authApi.getCmSubscriptionBenefits(channelId!, taskId!)
      if (response.kind === "not-found") {
        return null
      }
      if (response.kind !== "ok") {
        throw new Error(`Failed to fetch CM subscription benefits: ${response.kind}`)
      }
      return response.data
    },
    enabled: !!channelId && !!taskId,
    staleTime: Infinity,
    retry: false,
  })
}
